import React from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  Link, 
  IconButton, 
  Divider, 
  useTheme, 
  useMediaQuery 
} from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import ComingSoonTag from './ComingSoonTag';

const Footer: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const footerLinks = [
    {
      title: 'Company',
      links: [
        { name: 'About Us', url: '/about', active: true },
        { name: 'Our Team', url: '/team', active: true }
      ]
    },
    {
      title: 'Events',
      links: [
        { name: 'Browse Events', url: '/events', active: true },
        { name: 'Create Event', url: '/create-event', active: true },
        { name: 'Featured Events', url: '/featured', active: false }
      ]
    },
    {
      title: 'Support',
      links: [
        { name: 'Help Center', url: '/help', active: true },
        { name: 'FAQs', url: '/faq', active: true },
        { name: 'Terms of Service', url: '/terms', active: true },
        { name: 'Privacy Policy', url: '/privacy', active: true }
      ]
    }
  ];

  return (
    <Box
      sx={{
        bgcolor: theme.palette.primary.main,
        color: 'white',
        pt: 6,
        pb: 3,
        mt: 8
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={isMobile ? 4 : 8}>
          {/* Logo and Description */}
          <Grid item xs={12} md={4}>
            <Typography variant="h5" fontWeight="bold" mb={2}>
              MyEventShop
            </Typography>
            <Typography variant="body2" mb={3} sx={{ opacity: 0.8 }}>
              Discover and create amazing events in your area. 
              Join our community of event enthusiasts and never miss out on what's happening around you.
            </Typography>
            
            {/* Social Media Icons */}
            <Box display="flex" gap={1} mb={isMobile ? 2 : 0}>
              <IconButton color="inherit" aria-label="Facebook" size="small">
                <FacebookIcon />
              </IconButton>
              <IconButton color="inherit" aria-label="Twitter" size="small">
                <TwitterIcon />
              </IconButton>
              <IconButton color="inherit" aria-label="Instagram" size="small">
                <InstagramIcon />
              </IconButton>
              <IconButton color="inherit" aria-label="LinkedIn" size="small">
                <LinkedInIcon />
              </IconButton>
            </Box>
          </Grid>

          {/* Footer Links - Responsive Grid */}
          {footerLinks.map((section) => (
            <Grid item xs={12} sm={4} md={2} key={section.title}>
              <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                {section.title}
              </Typography>
              <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {section.links.map((link) => (
                  <Box component="li" key={link.name} mb={1}>
                    {link.active ? (
                      <Link 
                        href={link.url} 
                        color="inherit" 
                        underline="hover"
                        sx={{ opacity: 0.8, '&:hover': { opacity: 1 } }}
                      >
                        {link.name}
                      </Link>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            opacity: 0.6, 
                            cursor: 'not-allowed'
                          }}
                        >
                          {link.name}
                        </Typography>
                        <ComingSoonTag variant="chip" size="small" />
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Grid>
          ))}

        </Grid>

        <Divider sx={{ my: 4, bgcolor: 'rgba(255,255,255,0.1)' }} />
        
        {/* Copyright Section */}
        <Box 
          display="flex" 
          flexDirection={isMobile ? 'column' : 'row'} 
          justifyContent="space-between"
          alignItems={isMobile ? 'center' : 'flex-start'}
          textAlign={isMobile ? 'center' : 'left'}
          gap={isMobile ? 2 : 0}
        >
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            © {new Date().getFullYear()} MyEventShop. All rights reserved.
          </Typography>
          <Box display="flex" gap={3}>
            <Link href="/terms" color="inherit" underline="hover" sx={{ opacity: 0.7, fontSize: '0.875rem' }}>
              Terms
            </Link>
            <Link href="/privacy" color="inherit" underline="hover" sx={{ opacity: 0.7, fontSize: '0.875rem' }}>
              Privacy
            </Link>
            <Link href="/cookies" color="inherit" underline="hover" sx={{ opacity: 0.7, fontSize: '0.875rem' }}>
              Cookies
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer; 